import React, { useEffect, useState } from "react";
import { Bell, Plus, Clock } from "lucide-react";

/**
 * TopBar — sticky 60px-tall crystal-glass header above the main content column.
 *
 * Left:   breadcrumb (RailSync / Mission Control) + LIVE pill
 * Right:  IST clock pill + last-refreshed timestamp + bell + Track PNR CTA
 *
 * Props:
 *   onAddPnr           — primary Track PNR CTA handler
 *   lastRefreshedAt    — Date | null; renders the "Refreshed Xs ago" sub-pill
 *   onBellClick        — notification bell click handler
 *   hasNotifications   — bool; renders the red dot on the bell
 *   breadcrumbActive   — last breadcrumb segment label (default "Mission Control")
 */

const formatIST = (d) => {
  return d.toLocaleTimeString("en-IN", {
    hour: "2-digit",
    minute: "2-digit",
    hour12: false,
    timeZone: "Asia/Kolkata",
  });
};

const formatAgo = (date) => {
  if (!date) return null;
  const secs = Math.floor((Date.now() - date.getTime()) / 1000);
  if (secs < 60) return `${secs}s ago`;
  const mins = Math.floor(secs / 60);
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  return `${hrs}h ago`;
};

const TopBar = ({
  onAddPnr,
  lastRefreshedAt,
  onBellClick,
  hasNotifications = false,
  breadcrumbActive = "Mission Control",
}) => {
  const [now, setNow] = useState(new Date());
  useEffect(() => {
    const id = setInterval(() => setNow(new Date()), 1000 * 30);
    return () => clearInterval(id);
  }, []);

  const refreshedLabel = formatAgo(lastRefreshedAt);

  return (
    <div
      className="sticky top-0 z-40 backdrop-blur-xl bg-zinc-950/75 border-b border-zinc-800/60"
      data-testid="topbar"
    >
      <div className="flex items-center gap-4 px-6 sm:px-8 h-[60px]">
        {/* Breadcrumb */}
        <div className="flex items-center gap-2 text-[12px] text-zinc-500 whitespace-nowrap shrink-0 font-mono">
          <span className="text-zinc-600">RailSync</span>
          <span className="text-zinc-700">/</span>
          <span className="text-zinc-300">{breadcrumbActive}</span>
        </div>
        <span
          className="px-1.5 py-0.5 rounded text-[10px] bg-indigo-500/15 text-indigo-300 border border-indigo-500/30 shrink-0 font-mono"
          aria-label="Live session"
        >
          LIVE
        </span>

        <div className="flex-1" />

        {/* Clock + last refresh pill */}
        <div className="hidden sm:flex items-center gap-2 px-2.5 py-1 rounded-lg bg-zinc-900/70 border border-zinc-800 text-[11.5px] text-zinc-400 whitespace-nowrap shrink-0">
          <Clock size={12} />
          <span className="font-mono">{formatIST(now)} IST</span>
          {refreshedLabel && (
            <>
              <span className="w-px h-3 bg-zinc-800 mx-1" />
              <span className="text-zinc-500">Refreshed</span>
              <span className="text-emerald-400 font-mono">
                {refreshedLabel}
              </span>
            </>
          )}
        </div>

        {/* Notification bell */}
        <button
          type="button"
          onClick={onBellClick}
          className="w-9 h-9 shrink-0 rounded-lg bg-zinc-900/70 border border-zinc-800 hover:border-zinc-700 text-zinc-400 hover:text-zinc-100 flex items-center justify-center transition-colors relative"
          aria-label="Notifications"
          data-testid="topbar-bell"
        >
          <Bell size={16} />
          {hasNotifications && (
            <span className="absolute top-1.5 right-1.5 w-1.5 h-1.5 rounded-full bg-rose-400" />
          )}
        </button>

        {/* Primary CTA */}
        <button
          type="button"
          onClick={onAddPnr}
          className="h-9 px-3.5 shrink-0 whitespace-nowrap rounded-lg bg-indigo-500 hover:bg-indigo-400 text-white text-[13px] font-medium flex items-center gap-2 shadow-[0_0_22px_rgba(99,102,241,.35)] transition-colors"
          data-testid="topbar-track-pnr-btn"
        >
          <Plus size={14} strokeWidth={2.4} />
          <span>Track PNR</span>
          <kbd className="ml-1 px-1.5 py-0.5 rounded bg-white/15 text-white/90 text-[10px] font-mono">
            N
          </kbd>
        </button>
      </div>
    </div>
  );
};

export default TopBar;
